import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useSwitchVehicleDriverMutation } from "../../../store/api/school/ReservationSlice.js";
import {
  useGetAllActiveDriversQuery,
  useGetVendorDetailsQuery,
} from "../../../store/api/school/VendorSlice.js";
import {
  capitalizeString,
  convertTime,
  formatDateToLocal,
} from "../../../utils/dateAndTimeConversion.js";
import { getScheduleStatusInfo } from "../Driver/Schedules.js";

// Lets school/vendor/super_admin change the vehicle and/or driver on an
// already-scheduled reservation in place — no delete/recreate, no
// cancellation notice to parents. Only shown for non-pending reservations;
// BusingAssignDriver still handles the initial (pending -> scheduled) assignment.
const SwitchVehicleDriver = ({ bookingId, role, currentDriverId, currentVehicleId }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [driverId, setDriverId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [driverScheduleId, setDriverScheduleId] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [driverSchedule, setDriverSchedule] = useState([]);
  const [availableSchedules, setAvailableSchedules] = useState([]);

  const { data, isSuccess } = useGetAllActiveDriversQuery(undefined, {
    skip: !show,
    refetchOnMountOrArgChange: true,
  });
  const { data: vehiclesData, isSuccess: isSuccessVehicles } =
    useGetVendorDetailsQuery({}, { skip: !show });

  const [switchVehicleDriver, { isLoading }] = useSwitchVehicleDriverMutation();

  useEffect(() => {
    if (isSuccess && data?.data) setDrivers(data.data.drivers);
  }, [data, isSuccess]);

  useEffect(() => {
    if (isSuccessVehicles && vehiclesData?.data)
      setVehicles(vehiclesData.data.vehicles);
  }, [vehiclesData, isSuccessVehicles]);

  // Both dropdowns always open on the "keep current" placeholder rather
  // than pre-selecting the current vehicle/driver — the payload is only
  // built from an explicit choice (see handleSubmit), so this just keeps
  // the UI consistent/unambiguous about what "unchanged" looks like.
  useEffect(() => {
    if (show) {
      setDriverId("");
      setVehicleId("");
      setDriverScheduleId("");
    }
  }, [show]);

  // When a new driver is picked, show that driver's schedules — same as
  // BusingAssignDriver's initial-assignment flow — so the caller can pick
  // which schedule covers this reservation instead of relying purely on
  // the backend's best-effort auto-resolve.
  useEffect(() => {
    setDriverScheduleId("");
    if (driverId) {
      const selectedDriver = drivers.find(
        (d) => d?.driver_details?.driver_id === parseInt(driverId),
      );
      if (selectedDriver?.driver_details?.schedules) {
        setDriverSchedule(selectedDriver.driver_details.schedules);
        setAvailableSchedules(
          selectedDriver.driver_details.schedules.filter(
            (schedule) => schedule.status === "available",
          ),
        );
      } else {
        setDriverSchedule([]);
        setAvailableSchedules([]);
      }
    } else {
      setDriverSchedule([]);
      setAvailableSchedules([]);
    }
  }, [driverId, drivers]);

  const formatScheduleOption = (schedule) => {
    const shift =
      schedule.shift_type === "morning_afternoon"
        ? "Morning & Afternoon"
        : capitalizeString(schedule.shift_type);
    const startDate = formatDateToLocal(schedule.start_date);
    const endDate = formatDateToLocal(schedule.end_date);
    const startTime = convertTime(schedule.start_time);
    const endTime = convertTime(schedule.end_time);
    if (
      schedule.shift_type === "morning_afternoon" &&
      schedule.return_start_time &&
      schedule.return_end_time
    ) {
      const retStart = convertTime(schedule.return_start_time);
      const retEnd = convertTime(schedule.return_end_time);
      return `${shift} | ${startDate} – ${endDate} | Morning: ${startTime}–${endTime} | Afternoon: ${retStart}–${retEnd}`;
    }
    return `${shift} | ${startDate} ${startTime} – ${endDate} ${endTime}`;
  };

  const formatRecurrenceDays = (daysString) => {
    try {
      const days = JSON.parse(daysString);
      return days
        .map((day) => day.charAt(0).toUpperCase() + day.slice(1))
        .join(", ");
    } catch {
      return daysString;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast.dismiss();

    const payload = {};
    if (driverId && driverId !== String(currentDriverId)) {
      payload.driver_id = Number(driverId);
      if (driverScheduleId) {
        payload.driver_schedule_id = Number(driverScheduleId);
      }
    }
    if (vehicleId && vehicleId !== String(currentVehicleId)) {
      payload.vehicle_id = Number(vehicleId);
    }

    if (Object.keys(payload).length === 0) {
      toast.info(t("no-changes-selected") || "Nothing changed.");
      return;
    }

    try {
      const response = await switchVehicleDriver({
        id: bookingId,
        data: payload,
      }).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success(response.message);
        setShow(false);
      }
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  return (
    <>
      <Button size="sm" variant="outline-dark" onClick={() => setShow(true)}>
        {t("btns.switch") || "Switch Vehicle/Driver"}
      </Button>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        backdrop="static"
        className={`switchVehicleModel${driverSchedule.length > 0 ? " wideModal" : ""}`}
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="h5">
            {t("switch-vehicle-driver") || "Switch Vehicle / Driver"} #{bookingId}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "calc(90vh - 120px)", overflowY: "auto" }}>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="filedHd mb-2">{t("vehicle")}</label>
              <select
                className="formSelect form-select"
                value={vehicleId}
                onChange={(e) => setVehicleId(e.target.value)}
              >
                <option value="">{t("keep-current-vehicle") || "Keep current vehicle"}</option>
                {vehicles
                  ?.filter((item) => String(item?.id) !== String(currentVehicleId))
                  .map((item, i) => (
                    <option value={item?.id} key={i}>
                      {item?.vehicle_type?.name} — {item?.license_number}
                    </option>
                  ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="filedHd mb-2">{t("driver")}</label>
              <select
                className="formSelect form-select"
                value={driverId}
                onChange={(e) => setDriverId(e.target.value)}
              >
                <option value="">{t("keep-current-driver") || "Keep current driver"}</option>
                {drivers
                  // item.id is the driver's users.id (same space as
                  // currentDriverId, which comes from the reservation's
                  // driver.id) — item.driver_details.driver_id is a
                  // different id (drivers.id, used as the option's value
                  // for the switch payload), so it can't be compared here.
                  ?.filter((item) => String(item?.id) !== String(currentDriverId))
                  .map((item, i) => (
                    <option value={item?.driver_details?.driver_id} key={i}>
                      {item?.full_name}
                    </option>
                  ))}
              </select>
            </div>

            {driverId && (
              <div className="mb-3">
                <label className="filedHd mb-2">
                  {t("available")} {t("schedules")} ({t("status")}:{" "}
                  {t("available")}) <span className="text-danger">*</span>
                </label>

                {availableSchedules.length === 0 ? (
                  <div className="alert alert-warning p-2 mb-2 d-flex justify-content-between align-items-center">
                    <small>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {t("no-schedules-available-for-this-driver")}.
                    </small>
                    <Link
                      to={`/drivers/${driverId}/schedules`}
                      className="badge bg-secondary py-2"
                    >
                      {t("add")} {t("schedule")}
                    </Link>
                  </div>
                ) : (
                  <div className="schedules-list mb-3">
                    <select
                      className="formSelect form-select"
                      value={driverScheduleId}
                      onChange={(e) => setDriverScheduleId(e.target.value)}
                      required
                    >
                      <option value="">
                        {t("select")} {t("driver")} {t("schedule")}
                      </option>
                      {availableSchedules.map((schedule, i) => (
                        <option value={schedule.id} key={i}>
                          {formatScheduleOption(schedule)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {driverSchedule.length > 0 && (
                  <div className="all-schedules mt-3">
                    <h6 className="filedHd mb-2">
                      <i className="fas fa-calendar-alt me-2"></i>
                      {t("all")} {t("driver")} {t("schedules")}
                    </h6>
                    <div className="table">
                      <table className="">
                        <thead>
                          <tr>
                            <th>{t("shift")}</th>
                            <th>{t("start")}</th>
                            <th>{t("end")}</th>
                            <th>{t("recurrence")}</th>
                            <th>{t("status")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {driverSchedule.map((schedule, i) => {
                            const statusInfo = getScheduleStatusInfo(
                              schedule?.status,
                            );
                            return (
                              <tr key={i}>
                                <td>
                                  <span
                                    className={`badge ${
                                      schedule.shift_type === "morning"
                                        ? "bg-primary"
                                        : schedule.shift_type === "afternoon"
                                          ? "bg-warning text-dark"
                                          : "bg-info"
                                    }`}
                                  >
                                    {capitalizeString(schedule.shift_type)}
                                  </span>
                                </td>
                                <td>
                                  <small>
                                    {formatDateToLocal(schedule.start_date)}{" "}
                                    {convertTime(schedule.start_time)}
                                  </small>
                                  {schedule.shift_type === "morning_afternoon" &&
                                    schedule.return_start_time && (
                                      <div>
                                        <small className="text-muted">
                                          Aft:{" "}
                                          {convertTime(
                                            schedule.return_start_time,
                                          )}
                                        </small>
                                      </div>
                                    )}
                                </td>
                                <td>
                                  <small>
                                    {formatDateToLocal(schedule.end_date)}{" "}
                                    {convertTime(schedule.end_time)}
                                  </small>
                                  {schedule.shift_type === "morning_afternoon" &&
                                    schedule.return_end_time && (
                                      <div>
                                        <small className="text-muted">
                                          Aft:{" "}
                                          {convertTime(
                                            schedule.return_end_time,
                                          )}
                                        </small>
                                      </div>
                                    )}
                                </td>
                                <td>
                                  <small>
                                    {capitalizeString(schedule.recurrence_type)}
                                  </small>
                                  {schedule.recurrence_days &&
                                    schedule.recurrence_days !== "[]" && (
                                      <div>
                                        <small>
                                          {formatRecurrenceDays(
                                            schedule.recurrence_days,
                                          )}
                                        </small>
                                      </div>
                                    )}
                                </td>
                                <td>
                                  <span className={statusInfo.className}>
                                    {statusInfo.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            <p className="text-muted small mb-3">
              {t("switch-vehicle-driver-note") ||
                "The reservation and every rider's booking stay active — parents only get notified that the assigned vehicle/driver changed, not that anything was cancelled."}
            </p>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShow(false)} disabled={isLoading}>
                {t("btns.cancel") || "Cancel"}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  isLoading ||
                  (!driverId && !vehicleId) ||
                  (driverId && !driverScheduleId)
                }
              >
                {isLoading ? t("saving") || "Saving..." : t("btns.save") || "Save"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SwitchVehicleDriver;
